import { useMutation } from '@tanstack/react-query';

import { NEXTABLE_WAITLIST_ENDPOINT } from 'reservations/base/constants/endpoints';
import { HTTP_STATUS_CODE_403_UNAUTHORIZED } from 'reservations/base/constants/httpStatusCodes';
import { REDIRECT_WITH_ACCOUNT_DETAILS } from 'reservations/base/constants/redirects';
import is5xxError from 'reservations/base/helpers/is5xxError';
// NOTE: Fundamentally, Booking and Waitlist are all instances of a Party
import {
  type HandleErrorOptions,
  type Booking as Waitlist,
  type NextableBookingResponse as WaitlistResponse,
  BookingResponseSchema as WaitlistResponseSchema
} from 'reservations/booking/types';
import { cleanCreateWaitlistFormData } from 'reservations/waitlist/helpers/cleanWaitlistFormData';
import { type RequestError, restClient } from 'utility/restClient';

export type CreateWaitlistSuccessOptions = {
  waitlist: Waitlist;
  navUrl?: string;
};

type UseCreateWaitlistMutationParams = {
  slug: string;
  apiKey: string;
  handleSuccess: (_options: CreateWaitlistSuccessOptions) => void;
  handleError: (_options: HandleErrorOptions) => void;
};

function useCreateWaitlistMutation({
  slug,
  apiKey,
  handleSuccess,
  handleError
}: UseCreateWaitlistMutationParams) {
  return useMutation<WaitlistResponse, RequestError, Record<string, any>>({
    mutationFn: async formData => {
      const cleanedFormData = cleanCreateWaitlistFormData(formData);
      const response = await restClient.post(NEXTABLE_WAITLIST_ENDPOINT, {
        data: Object.fromEntries(cleanedFormData),
        nextableApiKey: apiKey
      });

      try {
        return WaitlistResponseSchema.parse(response);
      } catch (err) {
        console.error('useCreateWaitlistMutation: ', err);
        throw err;
      }
    },
    onSuccess: data => {
      const { booking: waitlist } = data;
      const { bookingId: waitlistId } = waitlist;

      handleSuccess({
        navUrl: `/locations/${slug}/waitlist/${waitlistId}`,
        waitlist
      });
    },
    onError: error => {
      if (is5xxError(error.code)) {
        console.error('[useCreateWaitlistMutation]: ', error.message);
        handleError({ navUrl: REDIRECT_WITH_ACCOUNT_DETAILS });
        return;
      }

      // istanbul ignore next
      if (error.code === HTTP_STATUS_CODE_403_UNAUTHORIZED) {
        handleError({ forceReload: true });
        return;
      }

      handleError({ navUrl: `/locations/${slug}`, code: error.code ?? 400 });
    }
  });
}

export { useCreateWaitlistMutation };
